import {
  MenuItem2 as BlueprintMenuItem2,
  MenuItem2Props as BlueprintMenuItem2Props,
} from '@blueprintjs/popover2';
import { Checkmark } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import {
  StaticAvatar,
  StaticAvatarProps,
} from 'bones/design-system/components/avatar/StaticAvatar';
import {
  InteractiveBadge,
  InteractiveBadgeProps,
} from 'bones/design-system/components/badge/InteractiveBadge';
import { Checkbox } from 'bones/design-system/components/controls/Checkbox';
import { BlankIcon } from 'bones/design-system/components/icons/BlankIcon';
import {
  Icon,
  IconComponentType,
} from 'bones/design-system/components/icons/Icon';
import { Body } from 'bones/design-system/components/typography/Body';

export type MenuItemProps = Omit<
  BlueprintMenuItem2Props,
  | 'icon'
  | 'intent'
  | 'label'
  | 'labelClassName'
  | 'labelElement'
  | 'multiline'
  | 'text'
> & {
  avatarProps?: Omit<StaticAvatarProps, 'active' | 'disabled' | 'size'>;
  badgeProps?: Omit<
    InteractiveBadgeProps,
    'active' | 'disabled' | 'selected' | 'size' | 'variant'
  >;
  'data-testid'?: never;
  checkbox?: boolean;
  leftIcon?: IconComponentType;
  rightIcon?: IconComponentType;
  testId?: string;
  text?: string;
  subText?: string;
};

export const MenuItem: React.VFC<MenuItemProps> = ({
  active,
  avatarProps,
  badgeProps,
  checkbox,
  children,
  className,
  disabled,
  selected,
  leftIcon,
  rightIcon,
  text,
  testId,
  subText,
  submenuProps,
  ...props
}) => (
  <BlueprintMenuItem2
    active={active}
    className={classNames(className, 'vb-menu-item', {
      'vb-menu-item--checkbox': checkbox || selected,
      'vb-menu-item-state--selected': selected,
    })}
    data-testid={testId}
    disabled={disabled}
    selected={selected}
    submenuProps={{
      ...submenuProps,
      className: classNames('vb-menu-items', submenuProps?.className),
    }}
    tagName="div"
    text={
      <>
        {leftIcon ? <Icon IconComponent={leftIcon} size="sm" /> : null}
        {checkbox ? (
          <Checkbox checked={selected} disabled={disabled} size="md" />
        ) : null}
        {!checkbox && !leftIcon ? (
          <Icon IconComponent={selected ? Checkmark : BlankIcon} size="md" />
        ) : null}
        {avatarProps ? (
          <StaticAvatar
            active={active}
            disabled={disabled}
            size="md"
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...avatarProps}
          />
        ) : null}
        <div className="vb-menu-item-text">
          <Body className="vb-menu-item-text-text" ellipsize size="md">
            {text}
          </Body>
          <Body className="vb-menu-item-text-subtext" ellipsize size="sm">
            {subText}
          </Body>
        </div>
        <div className="vb-menu-item-right">
          {badgeProps ? (
            <InteractiveBadge
              active={active}
              disabled={disabled}
              selected={selected}
              size="md"
              variant="light"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...badgeProps}
            />
          ) : null}
          {rightIcon ? <Icon IconComponent={rightIcon} size="sm" /> : null}
        </div>
      </>
    }
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  >
    {children}
  </BlueprintMenuItem2>
);
